"use client";

import React, { useEffect, useRef, useState } from "react";
import { useStepperContext } from "@/context/stepper-provider";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import { useStepperStore } from "@/store/stepper";
import { useDispatch } from "react-redux";
import { setActivePallet } from "@/redux/slices/pallet/palletSlice";
import { useLocation, useNavigate } from "react-router-dom";

interface StepperProps {
  isOffline?: boolean;
}

export default function Stepper({ isOffline = false }: StepperProps) {
  const { stepper, steps } = useStepperContext();
  const { current, goTo } = stepper;
  const [currentStep, setCurrentStep] = useState<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pathname = useLocation();
  const isOfflinePath = pathname.pathname.includes('/offline');

  // Filter out Summary and Images steps if in offline mode
  const filteredSteps = steps.filter(step => {
    if (isOfflinePath) {
      return !['Summary', 'Images'].includes(step.title);
    }
    return true;
  });

  useEffect(() => {
    if (current.id !== currentStep) {
      goTo(parseInt(currentStep || "0"));
    }
  }, [currentStep]);

  useEffect(() => {
    if (containerRef.current) {
      const activeStep = containerRef.current.querySelector('[data-current="true"]');
      if (activeStep) {
        activeStep.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [current.id]);

  return (
    <div
      ref={containerRef}
      className="w-full h-[65px] flex items-center relative justify-between gap-0 sm:gap-2 x-space shadow-md overflow-x-auto scrollbar-hide"
    >
      {filteredSteps.map((step) => {
        const isCompleted = parseInt(current.id) > parseInt(step.id);
        const isCurrent = current.id === step.id;

        return (
          <div
            key={step.id}
            className={cn("flex items-center bg-white relative min-w-fit gap-1 sm:gap-3")}
            data-current={isCurrent}
          >
            <div
              className={cn(
                "flex items-center gap-2 border-blue py-2 sm:py-5 px-1 sm:px-2",
                isCurrent && "border-b-2"
              )}
            >
              { 
              
              // isCompleted ? (
              //   <div className="bg-blue rounded-full p-1 flex-center">
              //     <Check size={16} color="#fff" />
              //   </div>
              // ) :
              (
                <div
                  className={cn(
                    "size-4 sm:size-6 flex-center rounded-full border-2",
                    isCurrent
                      ? "border-states-warning bg-white"
                      : "bg-stepper-inactive"
                  )}
                >
                  {isCurrent && (
                    <span className="size-1 sm:size-2 rounded-full bg-states-warning"></span>
                  )}
                </div>
              )}

              <p
                onClick={() => {
                  goTo(parseInt(step.id) - 1);
                  setCurrentStep((parseInt(step.id) - 1).toString());
                }}
                className={cn(
                  "semi-bold-16 text-placeholder relative whitespace-nowrap text-xs sm:text-sm cursor-pointer hover:text-blue group",
                  isCurrent && "text-blue",
                  isCompleted && "text-blue"
                )}
              >
                <span className="hidden sm:inline">{step.title}</span>
                <span className="sm:hidden">{step.id}</span>
                {/* <span className="absolute left-0 -bottom-6 bg-white p-1 rounded shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-xs whitespace-nowrap z-10">
                  {step.title}
                </span> */}
              </p>
            </div>

            {step.id !== (isOfflinePath ? "10" : "11") && (
              <div
                className={cn(
                  "transition-all duration-150 hidden sm:block ease-in-out w-[10px] sm:w-[15px] md:w-[20px] lg:w-[25px] xl:w-[30px] mt-2 h-2 border-dashed border-t-2 border-grey",
                  isCompleted && "border-blue border-solid border-t-2",
                  isCurrent && "block"
                )}
              ></div>
            )}
          </div>
        );
      })}
    </div>
  );
}
